<template>
  <div
    class="refonte-details-header"
    v-bind:class="props.class">
    <refonte-button
      class="details-back"
      variant="secondary"
      v-bind:icon_left="IconChevronLeft"
      v-on:click="back">
      Retour
    </refonte-button>
    <div class="details-header-title-section">
      <div class="details-header-title-section-top">
        <div
          class="details-header-title-section-top-tags"
          v-if="detail.type?.id">
          <refonte-tag v-bind:variant="typeMapping[detail.type.id]">
            {{
              detail.ficheType ? detail.ficheType?.label : detail.type?.label
            }}
          </refonte-tag>
          <refonte-tag
            v-if="detail.produitLienElearning"
            v-bind:variant="'e-learning'">
            elearning
          </refonte-tag>
        </div>
        <h1>
          {{ detail.titre }}
        </h1>
        <div
          class="details-header-title-section-top-sub-title"
          v-if="detail.sousTitre">
          {{ detail.sousTitre }}
        </div>
      </div>
      <div class="details-header-title-section-bottom">
        <div class="details-header-title-section-bottom-left">
          <div
            v-if="detail.sessionReference && detail.type.id !== 2"
            class="details-header-title-section-bottom-reference">
            {{ `Session ${detail.sessionReference}` }}
          </div>
          <IconSeparatorVertical
            v-if="
              detail.cataloguePubliee &&
              detail.sessionReference &&
              detail.type.id !== 2
            " />
          <refonte-button
            v-if="
              detail.sessionUrlWebinaire ||
              detail.sessionUrlParticipant?.[0]?.url
            "
            variant="success"
            v-bind:icon_left="IconOpenNewTab"
            v-bind:on_click="() => openSession(detail)">
            Accéder à la session
          </refonte-button>
          <refonte-button
            v-if="detail.cataloguePubliee"
            variant="secondary"
            v-bind:icon_left="IconOpenNewTab"
            v-bind:on_click="() => openFiche(detail)">
            Voir la fiche pédagogique
          </refonte-button>
          <IconSeparatorVertical v-if="detail.parcoursTitre" />
          <div
            v-if="detail.parcoursTitre"
            class="details-header-title-section-bottom-reference flex items-center gap-[4px]">
            <refonte-pastille
              status="entreprise"
              class="mr-[4px]" />
            Cette inscription fait partie du Parcours&nbsp;:
            <router-link
              v-bind:to="{
                query: { id: detail.id },
                path: `/inscription/formations-details`
              }"
              class="text-refonte_blue-600 hover:underline">
              {{ detail.parcoursTitre }}
            </router-link>
          </div>
          <IconSeparatorVertical v-if="detail.cursusInscriptionTitre" />
          <div
            v-if="detail.cursusInscriptionTitre"
            class="details-header-title-section-bottom-reference flex items-center gap-[4px]">
            <refonte-pastille
              status="cursus"
              class="mr-[4px]" />
            Cette inscription fait partie du Cursus&nbsp;:
            <router-link
              v-bind:to="{
                query: { id: detail.cursusInscriptionId },
                path: `/inscription/formations-cursus-details`
              }"
              class="text-refonte_blue-600 hover:underline">
              {{ detail.cursusInscriptionTitre }}
            </router-link>
          </div>
          <IconSeparatorVertical v-if="detail.mentionsAccessibilite" />
          <refonte-details-header-item
            text=""
            v-if="detail.mentionsAccessibilite">
            <template v-slot:default>
              <display-accessibilite-icons
                v-bind:mentions_accessibilite="detail.mentionsAccessibilite" />
            </template>
          </refonte-details-header-item>
        </div>
      </div>
      <div class="details-header-title-section-bottom">
        <div
          class="details-header-title-section-bottom-left"
          v-if="
            (detail.type.id === 2 || detail.produitLienElearning) &&
            detail?.moduleElearning?.accessible
          ">
          <refonte-button
            class="elearning-btn"
            v-bind:icon_left="IconOpenNewTab"
            v-bind:disabled="!!detail?.moduleElearning?.dateDemandeRetractation"
            v-tooltip="
              detail?.moduleElearning?.dateDemandeRetractation
                ? {
                    content:
                      'L\'accès au module e-learning est désactivé suite à votre demande de rétractation.',
                    placement: 'left'
                  }
                : {}
            "
            v-bind:on_click="open_elearning_module">
            Accéder au module
          </refonte-button>
          <refonte-button
            class="elearning-btn"
            v-bind:icon_left="IconOpenNewTab"
            v-if="
              detail.type.id === 2 &&
              detail?.moduleElearning?.accessible &&
              detail?.moduleElearning?.showRetractation &&
              !detail?.moduleElearning?.dateDemandeRetractation
            "
            v-bind:on_click="open_elearning_retractation_module">
            Se rétracter
          </refonte-button>
          <IconSeparatorVertical
            v-if="detail.type.id !== 2 || detail.produitLienElearning" />
          <div v-if="detail.type.id !== 2 || detail.produitLienElearning">
            <div
              v-if="detail.moduleElearning.dateFinAcces"
              class="details-header-title-section-bottom-reference">
              Elearning disponible du
              {{ detail.moduleElearning.dateDebutAcces }} au
              {{ detail.moduleElearning.dateFinAcces }}
            </div>

            <div
              v-else
              class="details-header-title-section-bottom-reference">
              Elearning disponible à partir du
              {{ detail.moduleElearning.dateDebutAcces }}
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="details-header-infos-section">
      <refonte-card class="details-header-infos-section-left">
        <div
          class="details-header-infos-section-left-items-container min-w-[17rem]">
          <div class="details-header-infos-section-left-container-items">
            <refonte-details-header-item
              v-bind:icon_left="IconDate"
              v-bind:text="formatDates(detail)"
              v-bind:additional_text="getAdditionalText(detail)" />
          </div>
          <div class="details-header-infos-section-left-container-items">
            <refonte-details-header-item
              v-bind:icon_left="IconHourglass"
              v-bind:text="`${dates_helpers.methods.formatMinutesToHours(detail.dureeMinutes)} (${detail.dureeJours}j)`" />
          </div>
        </div>
        <refonte-button
          class="ml-auto mr-2 whitespace-nowrap"
          variant="secondary"
          v-bind:icon_left="IconDayAgenda"
          v-if="![4, 8].includes(detail.type.id) && detail.dateHeureDebut"
          v-on:click="() => setCalendrierModalOpen(true)">
          Ajouter au calendrier
        </refonte-button>
        <refonte-separator
          v-if="detail?.site?.id || detail.lieuNom"
          class="details-header-infos-section-left-separator" />
        <div
          v-if="detail?.site?.id && detail?.site?.coordinates"
          v-on:click="
            () =>
              open_map_modal(
                concatAdress(detail.site),
                detail.site?.coordinates
              )
          "
          class="details-header-infos-section-left-address clickable">
          <div class="details-header-infos-section-left-address-icon">
            <IconLocation />
          </div>
          <div class="details-header-infos-section-left-address-text">
            <span class="font-bold">Lieu : {{ detail.site.denomination }}</span>
            <span>{{ detail.site.numeroVoie }}</span>
            <span>{{ `${detail.site.codePostal} ${detail.site.ville}` }}</span>
          </div>
        </div>
        <div
          v-else-if="detail?.site?.id"
          class="details-header-infos-section-left-address">
          <div class="details-header-infos-section-left-address-icon">
            <IconLocation />
          </div>
          <div class="details-header-infos-section-left-address-text">
            <span class="font-bold">Lieu : {{ detail.site.denomination }}</span>
            <span>{{ detail.site.numeroVoie }}</span>
            <span>{{ `${detail.site.codePostal} ${detail.site.ville}` }}</span>
          </div>
        </div>
        <div
          v-else-if="detail.lieuNom"
          class="details-header-infos-section-left-address">
          <div class="details-header-infos-section-left-address-icon">
            <IconLocation />
          </div>
          <div class="details-header-infos-section-left-address-text">
            <span class="font-bold">Lieu : {{ detail.lieuNom }}</span>
          </div>
        </div>
      </refonte-card>
      <refonte-card class="details-header-infos-section-right">
        <div class="details-header-infos-section-right-row">
          <refonte-details-header-item
            v-bind:icon_left="IconTrainingProvider"
            v-bind:text="
              detail.organisateur && `Organisé par ${detail.organisateur.nom}`
            " />
          <refonte-button
            class="ml-auto mr-2"
            variant="secondary"
            v-if="detail.organisateur"
            v-on:click="() => setContactModalOpen(true)">
            Contacter l’organisateur
          </refonte-button>
        </div>
        <refonte-details-header-item
          v-bind:icon_left="IconCertification"
          v-bind:text="
            detail.certifiant
              ? 'Formation certifiante'
              : 'Formation non certifiante'
          "
          v-bind:additional_text="
            detail.mode && detail.mode.label === 'Intra'
              ? detail.mode.label
              : ''
          " />
      </refonte-card>
    </div>
    <refonte-page-alert
      v-if="
        (detail.type.id === 2 || detail.produitLienElearning) &&
        detail?.moduleElearning?.accessible
      "
      v-bind:description_in_card="true"
      v-bind:alert_type="'warning'">
      <u>
        <strong>
          Attention : la mise à jour des informations de suivi de votre module
          elearning, dans votre Espace Participant, ainsi que la génération de
          votre attestation, peut prendre 24h.</strong
        >
      </u>
    </refonte-page-alert>
  </div>
  <refonte-modal-contact-organisateur
    v-bind:show="isContactModalOpen"
    v-if="detail.organisateur"
    v-bind:contact="detail.organisateur"
    v-bind:on_close="() => setContactModalOpen(false)" />

  <refonte-ajout-calendrier-modal
    v-bind:show="isAddCalendrierModalOpen"
    v-bind:reference="detail.sessionReference"
    v-bind:title="detail.titre"
    v-bind:description="detail.programmes?.[0]?.prestations?.[0]?.site"
    v-bind:location="detail.lieuNom"
    v-bind:start="detail.dateHeureDebut"
    v-bind:end="detail.dateHeureFin"
    v-bind:on_close="() => setCalendrierModalOpen(false)" />
</template>

<script setup lang="ts">
  import RefonteModalContactOrganisateur from '@/views/_espace/_refonte/gestion/_inscriptions-details/inscriptions-details-modal-contact-organisateur.vue';
  import { ref } from 'vue';
  import IconCertification from '@/assets/img/refonte/icons/information/icon-certification.svg?component';
  import IconChevronLeft from '@/assets/img/refonte/icons/display/icon-chevron-left.svg?component';
  import IconDate from '@/assets/img/refonte/icons/information/icon-date.svg?component';
  import IconHourglass from '@/assets/img/refonte/icons/information/icon-hourglass.svg?component';
  import IconLocation from '@/assets/img/refonte/icons/information/icon-location.svg?component';
  import IconOpenNewTab from '@/assets/img/refonte/icons/action/icon-open-new-tab.svg?component';
  import IconSeparatorVertical from '@/assets/img/refonte/icons/display/icon-separator-vertical.svg?component';
  import IconTrainingProvider from '@/assets/img/refonte/icons/users/icon-training-provider.svg?component';
  import IconDayAgenda from '@/assets/img/refonte/icons/information/icon-day-agenda.svg?component';
  import RefonteButton from '@/components/refonte/buttons/refonte-button.vue';
  import RefonteDetailsHeaderItem from '@/components/refonte/details/refonte-details-header-item.vue';
  import RefonteCard from '@/components/refonte/cards/refonte-card.vue';
  import RefonteSeparator from '@/components/refonte/separators/refonte-separator.vue';
  import dates_helpers from '@/mixin/dates_helpers';
  import RefonteTag from '@/components/refonte/tags/refonte-tag.vue';
  import { typeMapping } from '@/types/gestion/inscriptions-types';
  import { useRouter } from 'vue-router';
  import { FormationsDetail } from '@/types/participant/formations-details-types';
  import displayAccessibiliteIcons from '@/components/utils/display-accessibilite-icons.vue';
  import RefontePastille from '@/components/refonte/pastille/refonte-pastille.vue';
  import RefontePageAlert from '@/components/refonte/alerts/refonte-page-alert.vue';
  import RefonteAjoutCalendrierModal from '@/components/refonte/modals/ajout-calendrier-modal.vue';

  const props = withDefaults(
    defineProps<{
      detail: FormationsDetail;
      open_map_modal?: Function;
      class?: string;
      open_elearning_module?: Function;
      open_elearning_retractation_module?: Function;
    }>(),
    {
      class: ''
    }
  );
  const getAdditionalText = (detail: FormationsDetail) => {
    if (detail.dates && detail.dates.length === 1) {
      const date = detail.dates[0];
      if (date.matin && date.aprem) {
        return 'Journée entière';
      } else if (date.matin) {
        return 'Matin';
      } else {
        return 'Après-midi';
      }
    }
  };
  const openFiche = (detail: FormationsDetail) => {
    window.open(`${detail.catalogueUrl}/fiche/${detail.catalogueReference}`);
  };

  const openSession = (detail: FormationsDetail) => {
    if (detail?.sessionUrlWebinaire) {
      window.open(detail?.sessionUrlWebinaire);
    } else {
      window.open(detail?.sessionUrlParticipant?.[0]?.url);
    }
  };

  const formatDates = (detail: FormationsDetail) => {
    if (detail.dateDebut && detail.dateFin) {
      if (detail.dateDebut == detail.dateFin) {
        return `Le ${detail.dateDebut}`;
      } else {
        return `Du ${detail.dateDebut} au ${detail.dateFin}`;
      }
    }
    return undefined;
  };
  const isContactModalOpen = ref(false);
  const setContactModalOpen = (value: boolean) => {
    isContactModalOpen.value = value;
  };

  const isAddCalendrierModalOpen = ref(false);
  const setCalendrierModalOpen = (value: boolean) => {
    isAddCalendrierModalOpen.value = value;
  };

  const router = useRouter();
  const back = () => {
    if (window.history.state.back) {
      router.back();
    } else {
      router.push('/inscription/formations');
    }
  };

  const concatAdress = (site) => {
    return `${site.denomination} - ${site.numeroVoie}, ${site.codePostal} ${site.ville}`;
  };
</script>

<style lang="scss" scoped>
  .refonte-details-header {
    @apply flex flex-col items-start self-stretch;
    gap: 12px;

    .details-back {
      margin-bottom: 12px;
    }

    .details-header-title-section {
      @apply flex flex-col items-start self-stretch;
      color: #000;
      gap: 4px;
      padding: 0 12px;

      .details-header-title-section-top {
        @apply flex flex-col items-start justify-center self-stretch;
        gap: 2px;

        .details-header-title-section-top-tags {
          @apply flex flex-wrap items-center;
          gap: 4px;
        }
        .details-header-title-section-top-sub-title {
          @apply text-refonte-black-60;
        }
      }

      .details-header-title-section-bottom {
        @apply flex flex-wrap items-center justify-end self-stretch;
        gap: 12px;
        margin-top: 8px;

        .details-header-title-section-bottom-left {
          @apply flex grow flex-wrap items-center self-stretch;
          gap: 8px;

          .details-header-title-section-bottom-reference {
            font-size: 13px;
            font-weight: 400;
            line-height: 16px;
          }
        }
      }
    }

    .details-header-infos-section {
      @apply flex items-stretch self-stretch;
      gap: 12px;

      .details-header-infos-section-left {
        @apply flex-row items-start gap-0;
        flex: 1 0 0;

        .details-header-infos-section-left-items-container {
          @apply flex grow flex-col items-start;

          .details-header-infos-section-left-container-items {
            @apply flex flex-wrap items-start justify-items-start;
          }
        }

        .details-header-infos-section-left-separator {
          @apply items-stretch;

          :deep(.separator-content) {
            padding: 4px 3px;

            .separator-content-line {
              @apply bg-refonte_blue-300;
              opacity: 0.3;
              min-width: 2px;
              max-width: 2px;
              height: 100%;
            }
          }
        }

        .details-header-infos-section-left-address {
          @apply flex items-start;
          gap: 6px;
          padding: 10px;

          .details-header-infos-section-left-address-icon {
            @apply flex shrink-0 items-center justify-center;
            width: 16px;
            height: 16px;

            :deep(svg) {
              @apply text-refonte-black;
              width: 16px;
              height: 16px;
            }
          }

          .details-header-infos-section-left-address-text {
            @apply flex flex-col items-start;
            gap: 4px;

            span {
              font-size: 13px;
              font-weight: 400;
              line-height: 16px;

              &.font-bold {
                font-weight: 600;
              }
            }
          }
        }
      }

      .details-header-infos-section-right {
        @apply items-start gap-0;
        flex: 1 0 0;

        .details-header-infos-section-right-row {
          @apply flex flex-wrap items-center self-stretch;
        }
      }

      @media (max-width: 1279.98px) {
        @apply flex-col;
      }
      @media (max-width: 499.98px) {
        .details-header-infos-section-left {
          @apply flex-col;

          .details-header-infos-section-left-separator {
            @apply items-stretch;

            :deep(.separator-content) {
              padding: 3px;

              .separator-content-line {
                min-width: 24px;
                width: 100%;
                max-width: 100%;
                height: 2px;
              }
            }
          }
        }
      }
    }
  }
  .clickable:hover {
    @apply cursor-pointer bg-refonte-pale-violet-3;
    border-radius: 8px;
  }

  .elearning-btn {
    @apply bg-refonte-e-learning #{!important};
    &:disabled {
      @apply cursor-not-allowed bg-refonte-white opacity-20;
    }
  }
</style>
